import { Button, Card, CardHeader, makeStyles, shorthands, Text, tokens, Tooltip } from '@fluentui/react-components';
import { ChatAdd24Regular } from '@fluentui/react-icons';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IGptConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useConversations, useGptConfigurations, useUserPreferences } from '../../store/hooks';
import { GptIcon } from '../Common/GptIcon';

interface MenuCommandsProps {
    onNewChat: (gptConfiguration: IGptConfiguration) => void;
    showOnlyToggleMenuCommand?: boolean;
    onCloseMenuRequired?: () => void;
}

const useStyles = makeStyles({
    commands: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('10px')
    },

    card: {
        backgroundColor: tokens.colorNeutralBackground6,
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('12px'),
        ...shorthands.padding('12px'),
        width: '100%'
    },
    cardHeader: {
        gridTemplateRows: '100%',
        paddingLeft: '0px',
        marginLeft: '0px',
        minWidth: '0',
        flex: '1',
        width: '100%'
    },
    actionContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
    },
    gptIconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',        
        minWidth: '48px',        
    }
});

const MenuCommands: FC<MenuCommandsProps> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { onNewChat, showOnlyToggleMenuCommand, onCloseMenuRequired } = props;
    const { chats } = useConversations();
    const [isNewChatEnabled, setIsNewChatEnabled] = useState(false);
    const { gptConfigurations } = useGptConfigurations();
    const { userPreferences, dispatch } = useUserPreferences();
    const [addedGpts, setAddedGpts] = useState<IGptConfiguration[]>([]);

    useEffect(() => {
        // Look for the last conversation based on latest lastUpdateOn (or startedOn as fallback)
        // Create a copy before sorting to avoid mutating Redux state
        const lastConversation = [...chats].sort((a, b) => {
            const aDate = a.lastUpdateOn ?? a.startedOn;
            const bDate = b.lastUpdateOn ?? b.startedOn;
            return aDate > bDate ? -1 : 1;
        })[0];
        const isConversationEmpty = !lastConversation?.prompts || lastConversation.prompts.length === 0;
        setIsNewChatEnabled(!isConversationEmpty);
    }, [chats]);

    useEffect(() => {
        setAddedGpts(gptConfigurations.filter(x => userPreferences.addedGpts?.includes(x.id!) || x.isDefault).sort((a, b) => Number(b.isDefault) - Number(a.isDefault)))
    }, [userPreferences, gptConfigurations]);

    // If showing only toggle command (collapsed mode), don't render anything
    // since toggle is now handled by PageLayout
    if (showOnlyToggleMenuCommand) {
        return null;
    }

    return (
        <div className={classes.commands}>
            
                {addedGpts.map((gpt) => {
                    return <Card key={gpt.id} className={classes.card} orientation="horizontal">
                        <div className={classes.gptIconContainer}>
                            <GptIcon gptId={gpt.id} theme={'light'} />
                        </div>
                        <CardHeader
                            className={classes.cardHeader}
                            header={
                                !gpt.isDefault ? <Text weight='bold'>{gpt.name}</Text> : <Text/>
                            }
                            action={
                                <Tooltip content={t('menu.newChat')} relationship="label">
                                    <Button
                                        appearance="transparent"
                                        icon={<ChatAdd24Regular />}
                                        onClick={() => {
                                            onNewChat(gpt);
                                            // Close menu in mobile mode when creating new chat
                                            if (onCloseMenuRequired) {
                                                onCloseMenuRequired();
                                            }
                                        }}
                                    />
                                </Tooltip>
                            }
                        />
                    </Card>
                })}            
            
        </div>
    );
};

export default MenuCommands;
